import type { IconName } from '../nav'

/**
 * Iconos del sidebar. SVG inline de 17 px dibujados con `currentColor` y
 * opacidades por capa (0.4–0.9), como el resto del portal: no se agrega una
 * librería de iconos para once glifos, y así heredan el color del ítem activo
 * sin una regla extra.
 */
export const navIcons: Record<IconName, JSX.Element> = {
  // Bandeja: sobre abierto. Es la pantalla principal del asesor.
  inbox: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <path d="M1 6.5 8.5 1.5 16 6.5v8a1.5 1.5 0 0 1-1.5 1.5h-12A1.5 1.5 0 0 1 1 14.5z"
            fill="currentColor" opacity="0.5" />
      <path d="M1 6.5 8.5 11l7.5-4.5" stroke="currentColor" strokeWidth="1.5"
            strokeLinejoin="round" />
    </svg>
  ),

  // Mis expedientes: una carpeta con su visto. Lo completo, listo para bajar
  // (03-nov-2026). Mismo dibujo que los demás: 17 px y capas de opacidad.
  expedientes: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <path d="M1 4a1.5 1.5 0 0 1 1.5-1.5h3.6l1.6 1.8h6.8A1.5 1.5 0 0 1 16 5.8v8.7A1.5 1.5 0 0 1 14.5 16h-12A1.5 1.5 0 0 1 1 14.5z"
            fill="currentColor" opacity="0.5" />
      <path d="M5.5 10.2 7.6 12.3 11.8 8" stroke="currentColor" strokeWidth="1.6"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),

  // Desempeño: barras ascendentes.
  performance: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <rect x="1" y="10" width="3.4" height="6" rx="1" fill="currentColor" opacity="0.45" />
      <rect x="6.8" y="6" width="3.4" height="10" rx="1" fill="currentColor" opacity="0.7" />
      <rect x="12.6" y="2" width="3.4" height="14" rx="1" fill="currentColor" opacity="0.95" />
    </svg>
  ),

  // Plantillas: tarjetas apiladas.
  templates: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <rect x="1" y="2" width="11" height="13" rx="1.6" fill="currentColor" opacity="0.45" />
      <rect x="4.5" y="4.5" width="11.5" height="12" rx="1.6" fill="currentColor" opacity="0.9" />
      <path d="M7 8h6.5M7 11h4.5" stroke="var(--c-sidebar)" strokeWidth="1.2"
            strokeLinecap="round" />
    </svg>
  ),

  dashboard: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <rect x="0.5" y="0.5" width="7" height="7" rx="1.5" fill="currentColor" opacity="0.9" />
      <rect x="9.5" y="0.5" width="7" height="4.5" rx="1.5" fill="currentColor" opacity="0.55" />
      <rect x="9.5" y="7" width="7" height="9.5" rx="1.5" fill="currentColor" opacity="0.55" />
      <rect x="0.5" y="9.5" width="7" height="7" rx="1.5" fill="currentColor" opacity="0.55" />
    </svg>
  ),

  // Operación: dos burbujas — la cola de conversaciones del equipo.
  operation: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <circle cx="6" cy="8.5" r="5.5" fill="currentColor" opacity="0.5" />
      <circle cx="12" cy="8.5" r="4.5" fill="currentColor" opacity="0.9" />
    </svg>
  ),

  documents: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <rect x="2" y="1" width="10" height="13" rx="1.4" fill="currentColor" opacity="0.45" />
      <rect x="4.5" y="3.5" width="10" height="13" rx="1.4" fill="currentColor" opacity="0.9" />
    </svg>
  ),

  metrics: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <path d="M1.5 12.5 6 7.5l3.5 3 5.5-7" stroke="currentColor" strokeWidth="1.6"
            strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="15" cy="3.5" r="1.8" fill="currentColor" opacity="0.7" />
    </svg>
  ),

  // Agente IA: chispa.
  ai: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <path d="M8.5 1 10.2 6 15 7.7l-4.8 1.7L8.5 15l-1.7-5.6L2 7.7l4.8-1.7z"
            fill="currentColor" opacity="0.9" />
      <circle cx="14" cy="13.5" r="1.6" fill="currentColor" opacity="0.45" />
    </svg>
  ),

  // Canales y CRM: eslabones de una conexión.
  channels: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <rect x="1" y="6" width="7" height="5" rx="2.2" fill="none" stroke="currentColor"
            strokeWidth="1.5" opacity="0.6" />
      <rect x="9" y="6" width="7" height="5" rx="2.2" fill="none" stroke="currentColor"
            strokeWidth="1.5" />
      <line x1="7.4" y1="8.5" x2="9.6" y2="8.5" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  ),

  // Administración: sliders de configuración.
  admin: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <rect x="1" y="3" width="15" height="3.4" rx="1.7" fill="currentColor" opacity="0.4" />
      <circle cx="11" cy="4.7" r="2.6" fill="currentColor" />
      <rect x="1" y="10.3" width="15" height="3.4" rx="1.7" fill="currentColor" opacity="0.4" />
      <circle cx="6" cy="12" r="2.6" fill="currentColor" />
    </svg>
  ),

  // Observabilidad: pulso de monitoreo.
  observability: (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
      <circle cx="8.5" cy="8.5" r="7.5" stroke="currentColor" strokeWidth="1.4" opacity="0.5" />
      <path d="M3.5 8.5h2.8L7.7 5.5l1.8 6 1.3-3h2.7" stroke="currentColor" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
}

export const logoutIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
)
